import { useEffect, useRef } from 'react';
import { getSocket } from '@/lib/utils';
import { useExpireAuthData, useSetAuthData } from '@/hooks/auth';
import { useSetGlobalStatus } from '@/hooks/status';
import { useProcessUpdateAvailableEvent, useSetOfflineWarning } from '@/hooks/useWarningBar';
import { useProcessPlayerlistEvents } from '@/hooks/playerlist';
import { LogoutReasonHash } from '@/pages/auth/Login';
import { useDeploymentNotifications } from '@/hooks/useDeploymentNotifications';
import type { ListenEventsMap } from '@shared/socketioTypes';


/**
 * Responsible for starting ahd handling the main socket.io connection
 * This has been separated from the MainShell.tsx to avoid possible re-renders
 */
export default function MainSocket() {
    const deploymentNotifications = useDeploymentNotifications();
    const expireSession = useExpireAuthData();
    const setAuthData = useSetAuthData();
    const socketStateChangeCounter = useRef(0);
    const setIsSocketOffline = useSetOfflineWarning();
    const setGlobalStatus = useSetGlobalStatus();
    const processPlayerlistEvents = useProcessPlayerlistEvents();
    const processUpdateAvailableEvent = useProcessUpdateAvailableEvent();

    // Rejoin privileged subscriptions when administrator permissions change.
    useEffect(() => {
        //SocketIO
        const rooms = window.txConsts.isWebInterface ? ['status', 'playerlist'] : ['status'];
        if (deploymentNotifications.enabled) rooms.push('deployment');
        const socket = getSocket(rooms);
        let disconnectTimer: ReturnType<typeof setTimeout> | undefined;
        const handlers = {
            connect: () => {
                console.log("Main Socket.IO Connected.");
                socketStateChangeCounter.current += 1;
                clearTimeout(disconnectTimer);
                setIsSocketOffline(false);
                deploymentNotifications.refresh();
            },
            disconnect: (message) => {
                console.log("Main Socket.IO Disonnected:", message);
                //Grace period of 500ms to allow for quick reconnects
                //Tracking the state change ID for the timeout not to overwrite a reconnection
                const newId = socketStateChangeCounter.current + 1;
                socketStateChangeCounter.current = newId;
                clearTimeout(disconnectTimer);
                disconnectTimer = setTimeout(() => {
                    if (socketStateChangeCounter.current === newId) {
                        setIsSocketOffline(true);
                    }
                }, 500);
            },
            error: (error) => {
                console.log('Main Socket.IO', error);
            },
            logout: (reason) => {
                expireSession('main socketio', reason);
            },
            refreshToUpdate: () => {
                if (window.txConsts.isWebInterface) {
                    window.location.reload();
                    return;
                }
                expireSession('main socketio', 'got refreshToUpdate', LogoutReasonHash.UPDATED);
            },
            txAdminShuttingDown: () => {
                expireSession('main socketio', 'got txAdminShuttingDown', LogoutReasonHash.SHUTDOWN);
            },
            status: (status) => {
                setGlobalStatus(status);
            },
            playerlist: (playerlistData) => {
                if (!window.txConsts.isWebInterface) return;
                processPlayerlistEvents(playerlistData);
            },
            updateAvailable: (data) => {
                processUpdateAvailableEvent(data);
            },
            deployment: deploymentNotifications.receive,
            updateAuthData: (authData) => {
                console.warn('Got updateAuthData from websocket', authData);
                setAuthData(authData);
            },
        } satisfies Partial<ListenEventsMap> & { connect: () => void; disconnect: (message: string) => void };

        socket.on('connect', handlers.connect);
        socket.on('disconnect', handlers.disconnect);
        socket.on('error', handlers.error);
        socket.on('logout', handlers.logout);
        socket.on('refreshToUpdate', handlers.refreshToUpdate);
        socket.on('txAdminShuttingDown', handlers.txAdminShuttingDown);
        socket.on('status', handlers.status);
        socket.on('playerlist', handlers.playerlist);
        socket.on('updateAvailable', handlers.updateAvailable);
        socket.on('deployment', handlers.deployment);
        socket.on('updateAuthData', handlers.updateAuthData);

        return () => {
            socketStateChangeCounter.current += 1;
            clearTimeout(disconnectTimer);
            socket.off('connect', handlers.connect);
            socket.off('disconnect', handlers.disconnect);
            socket.off('error', handlers.error);
            socket.off('logout', handlers.logout);
            socket.off('refreshToUpdate', handlers.refreshToUpdate);
            socket.off('txAdminShuttingDown', handlers.txAdminShuttingDown);
            socket.off('status', handlers.status);
            socket.off('playerlist', handlers.playerlist);
            socket.off('updateAvailable', handlers.updateAvailable);
            socket.off('deployment', handlers.deployment);
            socket.off('updateAuthData', handlers.updateAuthData);
            socket.disconnect();
            setGlobalStatus(null);
        }
    }, [deploymentNotifications.enabled, deploymentNotifications.accountName,
        deploymentNotifications.receive, deploymentNotifications.refresh]);

    return null;
}
